import { fetchJSON, postJSON } from 'api/fetch';

import {
  createChannelApiRoute,
  getChannelInfoApiRoute,
  getQuickActionsApiRoute,
  getTeamLocationMembersApiRoute,
  getTokenApiRoute,
} from './apiRoutes';
import {
  ChannelInfoResponse,
  CreateNewChannelResponse,
  QuickActionsResponse,
  TeamLocationMembersResponse,
  TokenResponse,
} from './types';

/**
 * Retrieves an authentication token for the messenger service
 * @returns {Promise<TokenResponse>} Object containing the authentication token
 */
export const getToken = async (): Promise<TokenResponse> => {
  const res = await postJSON(getTokenApiRoute(), {});
  const token = res.token;
  return { token };
};

/**
 * Fetches team members for a specific location
 * @param {string} locationUUID - Unique identifier of the location
 * @returns {Promise<TeamLocationMembersResponse>} Team members data including
 * pagination information
 */
export const getTeamLocationMembers = async (
  locationUUID: string,
  cid: string,
  currentPage: number = 1,
  searchQuery?: string
): Promise<TeamLocationMembersResponse> => {
  const res = await fetchJSON(
    getTeamLocationMembersApiRoute(locationUUID, cid, currentPage, searchQuery)
  );
  const { users, per_page, page, total_count } = res;
  return { users, per_page, page, total_count };
};

/**
 * Creates a new messenger channel
 * @param {string | null} channelName - Name of the channel (null for system-generated name)
 * @param {string} locationUUID - Unique identifier of the location
 * @param {string[]} memberUUIDs - Array of team member UUIDs to add to the channel
 * @returns {Promise<CreateNewChannelResponse>} The created channel data
 */
export const createNewChannel = async (
  channelName: string | null,
  locationUUID: string,
  memberUUIDs: string[]
): Promise<CreateNewChannelResponse> => {
  const res = await postJSON(createChannelApiRoute(), {
    channel_name: channelName,
    location_uuid: locationUUID,
    member_uuids: memberUUIDs,
  });
  return res;
};

/**
 * Retrieves quick actions available for a location
 * @param {string} locationUUID - Unique identifier of the location
 * @returns {Promise<QuickActionsResponse>} Available channels and filters for quick actions
 */
export const getQuickActions = async (
  locationUUID: string,
  includeChannels: boolean = true,
  cid?: string
): Promise<QuickActionsResponse> => {
  const res = await fetchJSON(
    getQuickActionsApiRoute(locationUUID, includeChannels, cid)
  );
  return res;
};

/**
 * Fetches channel information including members and pagination details
 * @param {string} cid - The unique identifier of the channel
 * @param {number} [page] - Optional page number for paginated results
 * @returns {Promise<ChannelInfoResponse>} A promise that resolves to the channel
 *  information response
 * @throws {Error} If the API request fails
 */
export const getChannelInfo = async (
  cid: string,
  page?: number,
  searchQuery?: string
): Promise<ChannelInfoResponse> => {
  const res = await fetchJSON(getChannelInfoApiRoute(cid, page, searchQuery));
  return res;
};
